import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send } from "lucide-react";

export default function Contact() {
  const { profile } = useOutletContext() || {};
  const p = profile || {};
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/messages", form);
      toast.success("Message sent! Dr. Singh will get back to you soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send message.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="text-xs tracking-[0.3em] uppercase text-[#C24D2C]">Contact</p>
          <h1 className="mt-4 font-serif-display text-5xl lg:text-6xl text-[#1A2456] font-bold">Write to me.</h1>
          <p className="mt-6 text-[#5C544F] leading-relaxed">
            For collaborations, guest lectures, media enquiries or student mentorship — I would love to hear from you.
          </p>

          <div className="mt-10 space-y-5 text-sm">
            {p.email && (
              <div className="flex gap-3">
                <Mail size={18} className="mt-0.5 text-[#E35D29]" />
                <div>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#8A817C]">Email</p>
                  <p className="text-[#2D2825]">{p.email}</p>
                </div>
              </div>
            )}
            {p.phone && (
              <div className="flex gap-3">
                <Phone size={18} className="mt-0.5 text-[#E35D29]" />
                <div>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#8A817C]">Phone</p>
                  <p className="text-[#2D2825]">{p.phone}</p>
                </div>
              </div>
            )}
            {p.address && (
              <div className="flex gap-3">
                <MapPin size={18} className="mt-0.5 text-[#E35D29]" />
                <div>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#8A817C]">Address</p>
                  <p className="text-[#2D2825]">{p.address}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="bg-white border border-[#D8CFC4] p-8 lg:p-10 space-y-5"
          >
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Your name" testid="contact-name" value={form.name} onChange={set("name")} required />
              <Field label="Email" testid="contact-email" type="email" value={form.email} onChange={set("email")} required />
            </div>
            <Field label="Subject" testid="contact-subject" value={form.subject} onChange={set("subject")} />
            <div>
              <label className="text-[10px] tracking-[0.3em] uppercase text-[#8A817C]">Message *</label>
              <textarea
                data-testid="contact-message"
                required
                rows={6}
                value={form.message}
                onChange={set("message")}
                className="mt-2 w-full border border-[#D8CFC4] px-4 py-3 focus:outline-none focus:border-[#E35D29] focus:ring-1 focus:ring-[#E35D29] font-sans-body"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit"
              className="inline-flex items-center gap-2 bg-[#E35D29] text-white px-7 py-3.5 hover:bg-[#C84B1D] transition-colors disabled:opacity-60"
            >
              <Send size={16} /> {submitting ? "Sending…" : "Send message"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

const Field = ({ label, testid, required, type = "text", value, onChange }) => (
  <div>
    <label className="text-[10px] tracking-[0.3em] uppercase text-[#8A817C]">
      {label} {required && "*"}
    </label>
    <input
      data-testid={testid}
      required={required}
      type={type}
      value={value}
      onChange={onChange}
      className="mt-2 w-full border border-[#D8CFC4] px-4 py-3 focus:outline-none focus:border-[#E35D29] focus:ring-1 focus:ring-[#E35D29] font-sans-body"
    />
  </div>
);
